import React, { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'
import config from '../config'

const ServerPageStatusContext = createContext()

export const useServerPageStatus = () => {
  const context = useContext(ServerPageStatusContext)
  if (!context) {
    // Return fallback values when provider is not available
    return {
      pageStatuses: {},
      isLoading: false,
      isPageValid: () => false,
      areAllRequiredPagesValid: () => false,
      isSourceOfIncomeValid: () => false,
      refreshAllPageStatuses: () => {},
      checkPageStatus: () => Promise.resolve(false)
    }
  }
  return context
}

export const ServerPageStatusProvider = ({ children }) => {
  const [pageStatuses, setPageStatuses] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  // Function to check if a page has valid saved data on the server
  const checkPageStatus = async (pageKey, panNo, year) => {
    if (!panNo || !year) return false

    try {
      let endpoint = ''
      let hasValidData = false

      switch (pageKey) {
        case 'profile':
          const profileResponse = await axios.get(`${config.apiUrl}/user/profile/${panNo}/${year}`)
          hasValidData = profileResponse.data && 
            profileResponse.data.assessee && 
            profileResponse.data.assessee.firstName &&
            profileResponse.data.assessee.panNumber
          break

        case 'salary':
          const salaryResponse = await axios.get(`${config.apiUrl}/user/salary/${panNo}/${year}`)
          hasValidData = salaryResponse.data && 
            salaryResponse.data.salaryDetails && 
            salaryResponse.data.salaryDetails.length > 0 &&
            salaryResponse.data.salaryDetails.some(salary => 
              salary.employerName && salary.grossSalary > 0
            )
          break

        case 'house-property':
          const houseResponse = await axios.get(`${config.apiUrl}/user/houseproperty/${panNo}/${year}`)
          hasValidData = houseResponse.data && 
            houseResponse.data.houseDetails && 
            houseResponse.data.houseDetails.length > 0 &&
            houseResponse.data.houseDetails.some(house => 
              house.housePropertyIncome && house.housePropertyIncome.grossRentReceived > 0
            )
          break

        case 'capital-gain':
          const capitalResponse = await axios.get(`${config.apiUrl}/income/capitalgain/${panNo}/${year}`)
          hasValidData = capitalResponse.data && 
            capitalResponse.data.capitalGainDetails && 
            capitalResponse.data.capitalGainDetails.length > 0
          break

        case 'other-sources':
          const otherResponse = await axios.get(`${config.apiUrl}/user/othersources/${panNo}/${year}`)
          hasValidData = otherResponse.data && 
            otherResponse.data.otherSourcesDetails && 
            otherResponse.data.otherSourcesDetails.length > 0
          break

        case 'deduction-vi-a':
          const deductionResponse = await axios.get(`${config.apiUrl}/deduction/${panNo}/${year}`)
          hasValidData = deductionResponse.data && 
            deductionResponse.data.deductionDetails && 
            deductionResponse.data.deductionDetails.length > 0
          break

        case 'exempt-income':
          const exemptResponse = await axios.get(`${config.apiUrl}/income/exempt/${panNo}/${year}`)
          hasValidData = exemptResponse.data && 
            exemptResponse.data.exemptIncomeDetails && 
            exemptResponse.data.exemptIncomeDetails.length > 0
          break

        case 'tds-tcs':
          const tdsResponse = await axios.get(`${config.apiUrl}/taxpaid/${panNo}/${year}`)
          hasValidData = tdsResponse.data && 
            tdsResponse.data.taxPaid && 
            tdsResponse.data.taxPaid.length > 0
          break

        case 'filing-section':
          const filingResponse = await axios.get(`${config.apiUrl}/user/fillingsection/${panNo}/${year}`)
          hasValidData = filingResponse.data && 
            filingResponse.data.fillingSection && 
            filingResponse.data.fillingSection.receiptNo
          break

        default:
          return false
      }

      console.log(`checkPageStatus(${pageKey}):`, hasValidData)
      console.log(`checkPageStatus(${pageKey}) - Response data:`, endpoint ? 'API call made' : 'No endpoint')
      return hasValidData
    } catch (error) {
      console.log(`Error checking ${pageKey} status:`, error)
      return false
    }
  }

  // Function to refresh all page statuses
  const refreshAllPageStatuses = async () => {
    const panNo = localStorage.getItem('PAN_NO')
    const year = localStorage.getItem('YEAR')
    
    if (!panNo || !year) return

    // Don't set loading state to avoid blocking UI
    const newStatuses = {}

    const pageKeys = [
      'profile',
      'salary', 
      'house-property',
      'capital-gain',
      'other-sources',
      'deduction-vi-a',
      'exempt-income',
      'tds-tcs',
      'filing-section'
    ]

    // Check all pages in parallel with timeout to prevent blocking
    const statusPromises = pageKeys.map(async (pageKey) => {
      try {
        const hasValidData = await Promise.race([
          checkPageStatus(pageKey, panNo, year),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
        ])
        return { pageKey, hasValidData }
      } catch (error) {
        console.warn(`Failed to check status for ${pageKey}:`, error)
        return { pageKey, hasValidData: false }
      }
    })

    try {
      const results = await Promise.all(statusPromises)
      results.forEach(({ pageKey, hasValidData }) => {
        newStatuses[pageKey] = hasValidData
      })
      
      console.log('=== SERVER PAGE STATUS REFRESH ===')
      console.log('PAN:', panNo, 'YEAR:', year)
      console.log('Results:', results)
      console.log('New Statuses:', newStatuses)
      console.log('Green dots should now update based on these statuses')
      
      setPageStatuses(newStatuses)
    } catch (error) {
      console.error('Error refreshing page statuses:', error)
    }
  }

  // Function to check if a specific page has valid data
  const isPageValid = (pageKey) => {
    const isValid = pageStatuses[pageKey] === true
    console.log(`isPageValid(${pageKey}):`, isValid, 'pageStatuses:', pageStatuses)
    return isValid
  }

  // Function to check if all required pages are valid
  const areAllRequiredPagesValid = () => {
    const requiredPages = ['profile', 'salary', 'filing-section']
    return requiredPages.every(page => pageStatuses[page] === true)
  }

  // Function to check if any sub-items of source-of-income are valid
  const isSourceOfIncomeValid = () => {
    const sourcePages = ['salary', 'house-property', 'capital-gain', 'other-sources']
    const isValid = sourcePages.some(page => pageStatuses[page] === true)
    console.log(`isSourceOfIncomeValid():`, isValid, 'sourcePages:', sourcePages, 'pageStatuses:', pageStatuses)
    return isValid
  }

  // Refresh statuses when user logs in or PAN/YEAR changes
  useEffect(() => {
    const panNo = localStorage.getItem('PAN_NO')
    const year = localStorage.getItem('YEAR')
    
    if (panNo && year) {
      refreshAllPageStatuses()
    }
  }, [])

  // Listen for changes in PAN or YEAR
  useEffect(() => {
    const handleStorageChange = () => {
      const panNo = localStorage.getItem('PAN_NO')
      const year = localStorage.getItem('YEAR')
      
      if (panNo && year) {
        refreshAllPageStatuses()
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const value = {
    pageStatuses,
    isLoading,
    isPageValid,
    areAllRequiredPagesValid,
    isSourceOfIncomeValid,
    refreshAllPageStatuses,
    checkPageStatus
  }

  return (
    <ServerPageStatusContext.Provider value={value}>
      {children}
    </ServerPageStatusContext.Provider>
  )
}
